import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Chip } from "@mui/material";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const selectMenuProps = { PaperProps: { sx: { maxHeight: 320 } } };

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const EDIT_ROLE_ORDER = [
  ["EMPLOYEE", "Employee"],
  ["INTERN", "Intern"],
  ["CONSULTANT", "Consultant"],
  ["ADMIN", "Admin"],
  ["FINANCIAL_ADMIN", "Financial Admin"],
  ["MANAGER", "Manager"],
];

const PillChip = ({ label, tone = "neutral" }) => {
  const tones = {
    danger: { bg: ui.dangerSoft, color: ui.danger },
    success: { bg: ui.successSoft, color: ui.success },
    info: { bg: ui.primarySoft, color: ui.primaryDark },
    neutral: { bg: "#eef1f4", color: "#4b5563" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: "8px",
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const DetailSection = ({ title, children }) => (
  <Box>
    <Typography
      sx={{
        fontWeight: 700,
        color: ui.text,
        mb: 1.5,
        pl: 1.5,
        borderLeft: `4px solid ${ui.primary}`,
        lineHeight: 1.3,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const DetailItem = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: ui.muted }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 600,
        color: ui.text,
        fontSize: "0.9rem",
        wordBreak: "break-word",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const dialogPaperSx = {
  borderRadius: "16px",
  m: { xs: 2, sm: 4 },
};

const UserDialogs = ({
  // Edit / Delete dialog
  openModal,
  actionType,
  actionLoading,
  selectedUser,
  handleCloseModal,
  handleConfirmAction,
  editFullName,
  setEditFullName,
  editEmployeeId,
  setEditEmployeeId,
  editEmail,
  setEditEmail,
  editRole,
  setEditRole,
  editDepartment,
  setEditDepartment,
  editJobTitle,
  setEditJobTitle,
  departmentOptions,
  // Details dialog
  openDetailsModal,
  handleCloseDetailsModal,
  userDetails,
}) => {
  const isEdit = actionType === "Edit";
  const editDisabled =
    actionLoading ||
    (isEdit &&
      (!editFullName ||
        !editEmployeeId ||
        !editEmail ||
        !editRole ||
        !editJobTitle));

  return (
    <>
      {/* ---------------- Edit / Delete dialog ---------------- */}
      <Dialog
        open={openModal}
        onClose={actionLoading ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: 700,
            color: ui.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                actionType === "Delete" ? ui.dangerSoft : ui.primarySoft,
              color: actionType === "Delete" ? ui.danger : ui.primary,
            }}
          >
            {actionType === "Delete" ? (
              <WarningAmberRoundedIcon fontSize="small" />
            ) : (
              <EditOutlinedIcon fontSize="small" />
            )}
          </Box>
          {actionType === "Delete" ? "Delete user" : "Edit user"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {actionType === "Delete" ? (
            <Box>
              <Typography sx={{ mb: 2, color: ui.text }}>
                Are you sure you want to delete this user:{" "}
                <strong>{selectedUser?.fullName}</strong>? This action cannot be
                undone.
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: ui.surface,
                  border: `1px solid ${ui.border}`,
                }}
              >
                <Typography variant="body2" sx={{ color: ui.muted }}>
                  Employee ID
                </Typography>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedUser?.employeeId}
                </Typography>
                <Typography variant="body2" sx={{ color: ui.muted }}>
                  Job title
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {selectedUser?.jobTitle}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2.5,
                mt: 0.5,
              }}
            >
              <TextField
                label="Name*"
                value={editFullName}
                onChange={(e) => setEditFullName(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
              />
              <TextField
                label="Employee-ID*"
                value={editEmployeeId}
                onChange={(e) => setEditEmployeeId(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
              />
              <TextField
                label="Email*"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
              />
              <TextField
                label="Job Title*"
                value={editJobTitle}
                onChange={(e) => setEditJobTitle(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
              />
              <TextField
                select
                label="Role*"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {EDIT_ROLE_ORDER.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Department"
                value={editDepartment}
                onChange={(e) => setEditDepartment(e.target.value)}
                fullWidth
                sx={fieldSx}
                disabled={actionLoading}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {departmentOptions.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            disabled={actionLoading}
            sx={{ color: ui.muted, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={editDisabled}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : null
            }
            sx={{
              ...primaryButtonSx,
              py: 1,
              backgroundColor: isEdit ? ui.primary : ui.danger,
              "&:hover": {
                backgroundColor: isEdit ? ui.primaryDark : "#b71c1c",
              },
            }}
          >
            {isEdit ? "Save changes" : "Delete user"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Details dialog ---------------- */}
      <Dialog
        open={openDetailsModal}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: 700,
            color: ui.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: ui.primarySoft,
              color: ui.primary,
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </Box>
          User details
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {userDetails ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <DetailSection title="Basic information">
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: ui.surface,
                    border: `1px solid ${ui.border}`,
                  }}
                >
                  <DetailItem label="ID">{userDetails.id}</DetailItem>
                  <DetailItem label="Name">{userDetails.name}</DetailItem>
                  <DetailItem label="Email">{userDetails.email}</DetailItem>
                  <DetailItem label="Employee ID">
                    {userDetails.employeeId || "N/A"}
                  </DetailItem>
                  <DetailItem label="Role">{userDetails.role}</DetailItem>
                  <DetailItem label="Job title">
                    {userDetails.jobTitle}
                  </DetailItem>
                  <DetailItem label="Department">
                    {userDetails.department || "N/A"}
                  </DetailItem>
                  <DetailItem label="Created at">
                    {new Date(userDetails.createdAt).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }
                    )}
                  </DetailItem>
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <PillChip
                        label={userDetails.deleted ? "Deleted" : "Active"}
                        tone={userDetails.deleted ? "danger" : "success"}
                      />
                    </Box>
                  </Box>
                </Box>
              </DetailSection>

              <DetailSection title="Skills">
                {userDetails.skills?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {userDetails.skills.map((skill, index) => (
                      <PillChip key={index} label={String(skill)} tone="info" />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: ui.muted }}>None</Typography>
                )}
              </DetailSection>

              <DetailSection title="Languages">
                {userDetails.languages?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {userDetails.languages.map((language, index) => (
                      <PillChip
                        key={index}
                        label={String(language)}
                        tone="neutral"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: ui.muted }}>None</Typography>
                )}
              </DetailSection>

              <DetailSection title="Certificates">
                {userDetails.certificates?.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {userDetails.certificates.map((cert, index) => (
                      <Box key={index} sx={mobileCardSx}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: ui.text,
                            mb: 1,
                            wordBreak: "break-word",
                          }}
                        >
                          {cert.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "minmax(0, 1fr)",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 1.5,
                          }}
                        >
                          <DetailItem label="Organization">
                            {cert.organization}
                          </DetailItem>
                          <DetailItem label="Issue date">
                            {cert.issueDate}
                          </DetailItem>
                          <DetailItem label="Expiry date">
                            {cert.expiryDate || "N/A"}
                          </DetailItem>
                          <DetailItem label="URL">
                            {cert.url || "N/A"}
                          </DetailItem>
                          <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                            <DetailItem label="Comments">
                              {cert.comments || "None"}
                            </DetailItem>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: ui.muted }}>None</Typography>
                )}
              </DetailSection>

              <DetailSection title="Diplomas">
                {userDetails.diplomas?.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {userDetails.diplomas.map((diploma, index) => (
                      <Box key={index} sx={mobileCardSx}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: ui.text,
                            mb: 1,
                            wordBreak: "break-word",
                          }}
                        >
                          {diploma.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "minmax(0, 1fr)",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 1.5,
                          }}
                        >
                          <DetailItem label="Organization">
                            {diploma.organization}
                          </DetailItem>
                          <DetailItem label="Issue date">
                            {diploma.issueDate}
                          </DetailItem>
                          <DetailItem label="URL">
                            {diploma.url || "N/A"}
                          </DetailItem>
                          <DetailItem label="Comments">
                            {diploma.comments || "None"}
                          </DetailItem>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: ui.muted }}>None</Typography>
                )}
              </DetailSection>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: ui.primary }} />
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDetailsModal}
            sx={{ color: ui.muted, textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDialogs;